import React from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { TestTube, X, AlertTriangle } from 'lucide-react';

const TestingModeIndicator = () => {
  const { currentUser, switchUser } = useFamilyContext();

  // Only show if we're in a mock profile
  if (!currentUser?.isMock) return null;

  // Check if current mock user has valid UUID
  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(currentUser.id);
  if (!isValidUUID) {
    // Auto-exit invalid mock profiles
    console.warn('Invalid mock profile UUID detected, auto-exiting:', currentUser.id);
    const realUser = localStorage.getItem('real-user-context');
    if (realUser) {
      switchUser(JSON.parse(realUser));
      localStorage.removeItem('real-user-context');
    } else {
      window.location.href = '/college';
    }
    return null;
  }

  const handleExitTesting = () => {
    try {
      const realUser = localStorage.getItem('real-user-context');
      if (realUser) {
        switchUser(JSON.parse(realUser));
        localStorage.removeItem('real-user-context');
      } else {
        // Fallback: redirect to dashboard to reset context
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Error exiting testing mode:', error);
      // Fallback: redirect to dashboard to reset context
      window.location.href = '/dashboard';
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <TestTube className="w-4 h-4" />
              <span className="font-medium text-sm">Testing Mode Active</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-amber-100">
              <span className="text-sm">•</span>
              <span className="text-sm">
                Testing as: <strong>{currentUser.name}</strong>
              </span>
            </div>
          </div>
          
          <button
            onClick={handleExitTesting}
            className="flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded text-sm font-medium transition-colors"
          >
            <X className="w-3 h-3" />
            <span className="hidden sm:inline">Exit Testing</span>
            <span className="sm:hidden">Exit</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Error recovery component for when mock profiles cause issues
export const TestingErrorRecovery = ({ error, onRecover }) => {
  const { currentUser } = useFamilyContext();

  // Only show for mock profiles with errors
  if (!currentUser?.isMock || !error) return null;

  const handleRecover = () => {
    try {
      const realUser = localStorage.getItem('real-user-context');
      if (realUser) {
        const userData = JSON.parse(realUser);
        onRecover?.(userData);
        localStorage.removeItem('real-user-context');
      } else {
        // Fallback: redirect to dashboard
        window.location.href = '/dashboard';
      }
    } catch (recoveryError) {
      console.error('Error during recovery:', recoveryError);
      window.location.href = '/dashboard';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900">Testing Profile Error</h2>
        </div>
        
        <p className="text-slate-600 mb-4">
          The mock profile "<strong>{currentUser.name}</strong>" is causing issues with the application. 
          Would you like to return to your real account?
        </p>
        
        <div className="text-sm text-slate-500 mb-6 p-3 bg-slate-50 rounded-lg">
          <strong>Error:</strong> {error.message || 'Unknown error occurred'}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleRecover}
            className="flex-1 px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 font-medium"
          >
            Return to Real Account
          </button>
          <button
            onClick={() => window.location.href = '/college/parent-testing'}
            className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
          >
            Back to Testing Hub
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestingModeIndicator;